// react
import { useEffect, useRef } from 'react';

/**
 * Custom hook to scroll to error message when an error is set.
 * Used in dialog forms to ensure the user can see error messages at the bottom of the form.
 *
 * @param error - The current error state (string or null)
 * @returns A ref to attach to the error container element
 *
 * @example
 * ```tsx
 * const [error, setError] = useState<string | null>(null);
 * const errorRef = useScrollToError(error);
 *
 * // In JSX:
 * {error && (
 *   <Alert ref={errorRef} severity="error" sx={{ mt: 2 }}>
 *     {error}
 *   </Alert>
 * )}
 * ```
 */
export const useScrollToError = (error: string | null) => {
	const errorRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (error && errorRef.current) {
			// Small delay to ensure the DOM has updated
			requestAnimationFrame(() => {
				errorRef.current?.scrollIntoView({
					behavior: 'smooth',
					block: 'end',
				});
			});
		}
	}, [error]);

	return errorRef;
};
